import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { UXPContext } from "@core/context/Context";
import Icon from "@icon/Icon";
import PortalContainer from "./PortalContainer";
import { useOverlayStack } from "./overlay-stack";
// Matches $sheet-motion in _mobile_sheet.scss
const ANIMATION_MS = 250;
// Drag release: close past this share of the sheet height, or on a downward flick this fast (px per ms)
const CLOSE_DRAG_RATIO = 0.3;
const CLOSE_FLICK_SPEED = 0.5;
// Open sheets share one page lock: the html class stays while any sheet is open
let openSheets = 0;
const lockPageOverscroll = () => {
    if (openSheets++ === 0)
        document.documentElement.classList.add('uxp-mobilesheet-open');
};
const unlockPageOverscroll = () => {
    if (--openSheets === 0)
        document.documentElement.classList.remove('uxp-mobilesheet-open');
};
/**
 * @export
 * A panel that slides up from the bottom of the screen, for mobile layouts.
 *
 * Sits between the app header and footer. A tap outside the sheet, Escape, or dragging it down
 * (from anywhere, while its content is scrolled to the top) closes it. While it is open the page
 * does not pull-to-refresh.
 *
 * @example Fit to content
 * ```tsx
 * const [open, setOpen] = useState(false);
 * <Button title="Open" onClick={() => setOpen(true)} />
 * <MobileSheet isOpen={open} onClose={() => setOpen(false)}>
 *     <div style={{ padding: '1rem' }}>Sheet content</div>
 * </MobileSheet>
 * ```
 *
 * @example Full height with a title and close button
 * ```tsx
 * <MobileSheet isOpen={open} onClose={() => setOpen(false)} height="full" title="Filters" showClose>
 *     <div style={{ padding: '1rem' }}>Sheet content</div>
 * </MobileSheet>
 * ```
 *
 * @example Inside a panel
 * ```tsx
 * <div style={{ position: 'relative', height: '20rem' }}>
 *     <MobileSheet isOpen={open} onClose={() => setOpen(false)} contained>
 *         <div style={{ padding: '1rem' }}>Filters</div>
 *     </MobileSheet>
 * </div>
 * ```
 *
 * @category Overlays
 */
const MobileSheet = (props) => {
    const { isOpen, onClose, height = 'fit', contained = false, title, showClose = false, className, children } = props;
    const context = useContext(UXPContext);
    // Stays mounted while it slides down
    const [rendered, setRendered] = useState(isOpen);
    const [closing, setClosing] = useState(false);
    useEffect(() => {
        if (isOpen) {
            setRendered(true);
            setClosing(false);
            return;
        }
        if (!rendered)
            return;
        setClosing(true);
        const id = setTimeout(() => { setRendered(false); setClosing(false); }, ANIMATION_MS);
        return () => clearTimeout(id);
    }, [isOpen]);
    // Escape (and Back on phones) closes the topmost overlay only
    const isTopOverlay = useOverlayStack(isOpen, onClose);
    useEffect(() => {
        if (!isOpen)
            return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape' && isTopOverlay())
                onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);
    // While any sheet is open the page does not pull-to-refresh or bounce
    useEffect(() => {
        if (!isOpen)
            return;
        lockPageOverscroll();
        return unlockPageOverscroll;
    }, [isOpen]);
    // Drag down to close. Moves the sheet directly, no re-render per move.
    const sheetRef = useRef(null);
    // The touch listeners outlive a render: read the latest onClose
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const drag = useRef(null);
    const startDrag = (y) => {
        drag.current = { startY: y, startTime: Date.now(), dy: 0 };
        sheetRef.current.style.transition = 'none';
    };
    const moveDrag = (y) => {
        drag.current.dy = Math.max(0, y - drag.current.startY);
        sheetRef.current.style.transform = `translateY(${drag.current.dy}px)`;
    };
    const endDrag = () => {
        if (!drag.current)
            return;
        const { dy, startTime } = drag.current;
        drag.current = null;
        const sheet = sheetRef.current;
        const speed = dy / Math.max(1, Date.now() - startTime);
        sheet.style.transition = '';
        if (dy > sheet.offsetHeight * CLOSE_DRAG_RATIO || (dy > 10 && speed > CLOSE_FLICK_SPEED)) {
            // Slide the rest of the way from where it was let go
            sheet.style.transform = 'translateY(100%)';
            onCloseRef.current();
        }
        else {
            sheet.style.transform = '';
        }
    };
    // Touch: drag from anywhere on the sheet while its lists are scrolled to the top.
    // Listeners are not passive so the drag can stop the page scrolling and pull-to-refresh.
    useEffect(() => {
        const sheet = sheetRef.current;
        if (!rendered || !sheet)
            return;
        let start = null;
        let dragging = false;
        const onTouchStart = (e) => {
            start = null;
            dragging = false;
            const target = e.target;
            // Touches on a sheet opened inside this one belong to that sheet
            if (e.touches.length !== 1 || target.closest('.uxpcore_mobilesheet__container, .uxpcore_mobilesheet__layer') !== sheet)
                return;
            // A list scrolled down scrolls first
            for (let el = target; el && el !== sheet; el = el.parentElement) {
                if (el.scrollTop > 0)
                    return;
            }
            start = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        };
        const onTouchMove = (e) => {
            if (!start)
                return;
            const { clientX, clientY } = e.touches[0];
            if (!dragging) {
                const dx = clientX - start.x, dy = clientY - start.y;
                if (Math.abs(dx) < 6 && Math.abs(dy) < 6)
                    return;
                // Sideways or upward: leave it to the page
                if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
                    start = null;
                    return;
                }
                dragging = true;
                startDrag(start.y);
            }
            e.preventDefault();
            moveDrag(clientY);
        };
        const onTouchEnd = () => {
            if (dragging)
                endDrag();
            start = null;
            dragging = false;
        };
        sheet.addEventListener('touchstart', onTouchStart, { passive: true });
        sheet.addEventListener('touchmove', onTouchMove, { passive: false });
        sheet.addEventListener('touchend', onTouchEnd);
        sheet.addEventListener('touchcancel', onTouchEnd);
        return () => {
            sheet.removeEventListener('touchstart', onTouchStart);
            sheet.removeEventListener('touchmove', onTouchMove);
            sheet.removeEventListener('touchend', onTouchEnd);
            sheet.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [rendered]);
    // Mouse: drag the notch or header of a fit sheet (touch is handled above)
    const canMouseDrag = height === 'fit';
    const dragHandlers = canMouseDrag ? {
        onPointerDown: (e) => {
            if (e.pointerType === 'touch' || e.target.closest('button'))
                return;
            e.currentTarget.setPointerCapture(e.pointerId);
            startDrag(e.clientY);
        },
        onPointerMove: (e) => { if (drag.current)
            moveDrag(e.clientY); },
        onPointerUp: endDrag,
        onPointerCancel: endDrag,
    } : {};
    if (!rendered)
        return null;
    const sheet = _jsxs("div", { ref: sheetRef, role: "dialog", "aria-modal": "true", className: classNames("uxpcore_mobilesheet__container", `uxpcore_mobilesheet__container--${height}`, { "uxpcore_mobilesheet__container--closing": closing }, className), children: [canMouseDrag && _jsx("div", Object.assign({ className: "uxpcore_mobilesheet__handle" }, dragHandlers, { children: _jsx("span", {}) })), (title || showClose) && _jsxs("div", Object.assign({ className: "uxpcore_mobilesheet__header" }, dragHandlers, { children: [_jsx("div", { className: "uxpcore_mobilesheet__title", children: title }), showClose && _jsx("button", { className: "uxpcore_mobilesheet__close", onClick: onClose, "aria-label": context.$L('uxp-core.text.close'), children: _jsx(Icon, { icon: ['phl', 'x'] }) })] })), _jsx("div", { className: "uxpcore_mobilesheet__content", children: children })] });
    const dim = height === 'fit' && _jsx("div", { className: classNames("uxpcore_mobilesheet__dim", { "uxpcore_mobilesheet__dim--closing": closing }) });
    // Inside the parent: its own layer catches taps outside the sheet
    if (contained)
        return _jsxs("div", { className: "uxpcore_mobilesheet__layer", onClick: (e) => { e.stopPropagation(); if (e.target === e.currentTarget)
                onClose(); }, children: [dim, sheet] });
    return _jsxs(PortalContainer, { hasBackdrop: true, onClickBackdrop: onClose, className: "uxpcore_mobilesheet__backdrop", children: [dim, sheet] });
};
export default MobileSheet;
